import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TicketsAPI } from "../../api/tickets";

function buildTicketListUrl(filters = {}) {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, value);
    }
  });

  const qs = params.toString();
  return `/helpdesk/tickets${qs ? `?${qs}` : ""}`;
}

function CountCard({ title, value, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border rounded-lg p-4 text-left bg-white hover:bg-slate-50"
    >
      <div className="text-sm text-slate-500">{title}</div>
      <div className="text-3xl font-semibold mt-1">{value}</div>
    </button>
  );
}

function BarList({ title, items, labelKey, valueKey = "count", onItemClick, emptyText }) {
  const max = Math.max(...items.map((i) => Number(i[valueKey] || 0)), 0);

  return (
    <div className="border rounded-lg p-4 bg-white">
      <h2 className="text-lg font-semibold mb-4">{title}</h2>

      {items.length === 0 ? (
        <div className="text-sm text-slate-500">{emptyText}</div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const value = Number(item[valueKey] || 0);
            const widthPct = max > 0 ? `${(value / max) * 100}%` : "0%";

            return (
              <button
                key={`${item[labelKey]}-${item[valueKey]}`}
                type="button"
                onClick={() => onItemClick(item)}
                className="w-full text-left"
              >
                <div className="flex justify-between text-sm mb-1">
                  <span>{item[labelKey] || "Unknown"}</span>
                  <span className="font-medium">{value}</span>
                </div>
                <div className="h-3 bg-slate-100 rounded">
                  <div
                    className="h-3 bg-blue-500 rounded"
                    style={{ width: widthPct }}
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function HelpdeskDashboard() {
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState({
    totals: {
      total_tickets: 0,
      open_tickets: 0,
      stale_tickets: 0,
      unassigned_open_tickets: 0,
    },
    open_by_school: [],
    open_by_assignee: [],
    raised_by_users: [],
    by_department: [],
    by_category: [],
    by_subcategory: [],
    by_priority: [],
    by_status: [],
    stale_tickets: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

    useEffect(() => {
    async function load({ showLoading = false } = {}) {
      if (showLoading) setLoading(true);
      setError("");

      try {
        const data = await TicketsAPI.dashboard();

        setDashboard({
          totals: data.totals || {
            total_tickets: 0,
            open_tickets: 0,
            stale_tickets: 0,
            unassigned_open_tickets: 0,
          },
          open_by_school: data.open_by_school || [],
          open_by_assignee: data.open_by_assignee || [],
          by_category: data.by_category || [],
          by_status: data.by_status || [],
          stale_tickets: data.stale_tickets || [],
          raised_by_users: data.raised_by_users || [],
          by_department: data.by_department || [],
          by_subcategory: data.by_subcategory || [],
          by_priority: data.by_priority || [],
        });
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load helpdesk dashboard");
      } finally {
        setLoading(false);
      }
    }

    load({ showLoading: true });

    const interval = setInterval(() => {
      load();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="p-6">Loading helpdesk dashboard…</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Helpdesk Dashboard</h1>
        <p className="text-sm text-slate-600 mt-1">
          Click any card, bar, or ticket to open the relevant filtered ticket list.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CountCard
          title="Unassigned open tickets"
          value={dashboard.totals.unassigned_open_tickets || 0}
          onClick={() =>
            navigate(
              buildTicketListUrl({
                assigned_to: "__unassigned__",
                open_only: "1",
              })
            )
          }
        />

        <CountCard
          title="Open tickets"
          value={dashboard.totals.open_tickets || 0}
          onClick={() =>
            navigate(
              buildTicketListUrl({
                open_only: "1",
              })
            )
          }
        />

        <CountCard
          title="Not updated in 7+ days"
          value={dashboard.totals.stale_tickets || 0}
          onClick={() =>
            navigate(
              buildTicketListUrl({
                stale_days: "7",
              })
            )
          }
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <BarList
          title="Open tickets per school"
          items={dashboard.open_by_school}
          labelKey="school_name"
          onItemClick={(item) =>
            navigate(
              buildTicketListUrl({
                school_id: item.school_id,
                open_only: "1",
              })
            )
          }
          emptyText="No open tickets by school"
        />

        <BarList
          title="Users with highest open tickets"
          items={dashboard.open_by_assignee}
          labelKey="user_name"
          onItemClick={(item) =>
            navigate(
              buildTicketListUrl({
                assigned_to: item.user_id,
                open_only: "1",
              })
            )
          }
          emptyText="No assigned open tickets"
        />

        <BarList
          title="Users with highest raised by tickets"
          items={dashboard.raised_by_users}
          labelKey="user_name"
          onItemClick={(item) =>
            navigate(
              buildTicketListUrl({
                raised_by: item.user_id,
              })
            )
          }
          emptyText="No requester data"
        />

        <BarList
          title="Tickets by department"
          items={dashboard.by_department}
          labelKey="department_name"
          onItemClick={(item) =>
            navigate(
              buildTicketListUrl({
                department_id: item.department_id,
              })
            )
          }
          emptyText="No department data"
        />

        <BarList
          title="Tickets by category"
          items={dashboard.by_category}
          labelKey="category_name"
          onItemClick={(item) =>
            navigate(
              buildTicketListUrl({
                category_id: item.category_id,
              })
            )
          }
          emptyText="No category data"
        />

        <BarList
            title="Top subcategories"
            items={dashboard.by_subcategory}
            labelKey="subcategory_name"
            onItemClick={(item) =>
              navigate(
                buildTicketListUrl({
                  subcategory_id: item.subcategory_id,
                })
              )
            }
            emptyText="No subcategory data"
          />

        <BarList
          title="Tickets by status"
          items={dashboard.by_status}
          labelKey="status_name"
          onItemClick={(item) =>
            navigate(
              buildTicketListUrl({
                status_id: item.status_id,
              })
            )
          }
          emptyText="No status data"
        />

        <BarList
          title="Tickets by priority"
          items={dashboard.by_priority}
          labelKey="priority_name"
          onItemClick={(item) =>
            navigate(
              buildTicketListUrl({
                priority_id: item.priority_id,
              })
            )
          }
          emptyText="No priority data"
        />
      </div>

      <div className="border rounded-lg p-4 bg-white">
        <h2 className="text-lg font-semibold mb-4">
          Tickets not updated for more than 7 days
        </h2>

        {dashboard.stale_tickets.length === 0 ? (
          <div className="text-sm text-slate-500">No stale tickets</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Title</th>
                  <th className="px-3 py-2 text-left">School</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Last updated</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.stale_tickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    className="border-t hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/helpdesk/tickets/${ticket.id}`)}
                  >
                    <td className="px-3 py-2">{ticket.id}</td>
                    <td className="px-3 py-2">{ticket.title}</td>
                    <td className="px-3 py-2">{ticket.school_name || "-"}</td>
                    <td className="px-3 py-2">{ticket.status_name || "-"}</td>
                    <td className="px-3 py-2">
                      {ticket.updated_at
                        ? new Date(ticket.updated_at).toLocaleString()
                        : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}